/**
 * Types of interactions that can occur between game layers.
 */
export enum InteractionType {
  /** Fire-and-forget notification (uses EventBus) */
  EVENT = 'event',

  /** Move an entity (ant, car, etc.) between layers via portal */
  ENTITY_TRANSFER = 'entity_transfer',

  /** Query another layer's state (synchronous request/response) */
  STATE_QUERY = 'state_query',

  /** Visual/audio effect that spans multiple layers */
  EFFECT = 'effect',
}

/**
 * Base interface for layer interactions.
 */
export interface LayerInteraction {
  /** Type of interaction */
  type: InteractionType;

  /** Layer initiating the interaction */
  sourceLayer: string;

  /** Target layer(s) - use 'broadcast' to send to all layers */
  targetLayer: string | 'broadcast';

  /** Interaction-specific data */
  payload: unknown;

  /** Timestamp when interaction was created */
  timestamp: number;
}

/**
 * Event interaction - fire-and-forget notification.
 */
export interface EventInteraction extends LayerInteraction {
  type: InteractionType.EVENT;
  payload: {
    eventType: string;
    data: unknown;
  };
}

/**
 * Entity transfer interaction - move entity between layers.
 */
export interface EntityTransferInteraction extends LayerInteraction {
  type: InteractionType.ENTITY_TRANSFER;
  payload: {
    entityType: string;
    entityData: unknown;
    sourcePortalId: string;
    targetPortalId: string;
  };
}

/**
 * State query interaction - request information from another layer.
 */
export interface StateQueryInteraction extends LayerInteraction {
  type: InteractionType.STATE_QUERY;
  payload: {
    queryType: string;
    params?: Record<string, unknown>;
  };
}

/**
 * State query response.
 */
export interface StateQueryResponse {
  queryType: string;
  sourceLayer: string;
  data: unknown;
  timestamp: number;
}

/**
 * Effect interaction - visual/audio effect spanning layers.
 */
export interface EffectInteraction extends LayerInteraction {
  type: InteractionType.EFFECT;
  payload: {
    effectType: string;
    /** Position in normalized coordinates (0-1) for cross-layer positioning */
    position?: { x: number; y: number };
    duration?: number;
    intensity?: number;
    data?: unknown;
  };
}

/**
 * Pre-defined effect types for common cross-layer effects.
 */
export enum EffectType {
  /** Vibration/shake effect (e.g., from construction) */
  VIBRATION = 'vibration',

  /** Sound effect */
  SOUND = 'sound',

  /** Visual flash/highlight */
  FLASH = 'flash',

  /** Particle effect */
  PARTICLES = 'particles',
}

/**
 * Pre-defined query types for common state queries.
 */
export enum QueryType {
  /** Get population count */
  POPULATION = 'population',

  /** Get territory/coverage percentage */
  TERRITORY = 'territory',

  /** Get resource levels (food, materials, etc.) */
  RESOURCES = 'resources',

  /** Get alert/danger status */
  ALERT_STATUS = 'alert_status',

  /** Get specific entity by ID */
  GET_ENTITY = 'get_entity',
}

/**
 * Factory functions for creating interactions.
 */
export const Interaction = {
  event(
    sourceLayer: string,
    targetLayer: string | 'broadcast',
    eventType: string,
    data: unknown
  ): EventInteraction {
    return {
      type: InteractionType.EVENT,
      sourceLayer,
      targetLayer,
      payload: { eventType, data },
      timestamp: performance.now(),
    };
  },

  entityTransfer(
    sourceLayer: string,
    targetLayer: string,
    entityType: string,
    entityData: unknown,
    sourcePortalId: string,
    targetPortalId: string
  ): EntityTransferInteraction {
    return {
      type: InteractionType.ENTITY_TRANSFER,
      sourceLayer,
      targetLayer,
      payload: {
        entityType,
        entityData,
        sourcePortalId,
        targetPortalId,
      },
      timestamp: performance.now(),
    };
  },

  query(
    sourceLayer: string,
    targetLayer: string,
    queryType: string,
    params?: Record<string, unknown>
  ): StateQueryInteraction {
    return {
      type: InteractionType.STATE_QUERY,
      sourceLayer,
      targetLayer,
      payload: { queryType, params },
      timestamp: performance.now(),
    };
  },

  effect(
    sourceLayer: string,
    targetLayer: string | 'broadcast',
    effectType: string,
    options?: {
      position?: { x: number; y: number };
      duration?: number;
      intensity?: number;
      data?: unknown;
    }
  ): EffectInteraction {
    return {
      type: InteractionType.EFFECT,
      sourceLayer,
      targetLayer,
      payload: {
        effectType,
        ...options,
      },
      timestamp: performance.now(),
    };
  },
};
